import type { CSSProperties, ReactNode } from 'react';
import { Table } from '@mantine/core';
import { ColumnResizeHandle } from '../../common/ConfigurableTable/ConfigurableTable';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ColumnWidthsLayout } from '../useDataLibraryTableLayout';
import { withColumnWidth } from '../columnState';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';

interface ResizableHeaderCellProps {
  column: ColumnConfig;
  /** The first column stays pinned on horizontal scroll */
  sticky?: boolean;
  /** The column's default style (min width, wrapping) */
  style?: CSSProperties;
  layout: ColumnWidthsLayout;
  children: ReactNode;
}

/**
 * MED-1308: a Data Library header cell with a drag handle on its right edge.
 * Body cells take the same width through withColumnWidth.
 */
export function ResizableHeaderCell({
  column,
  sticky = false,
  style,
  layout,
  children,
}: ResizableHeaderCellProps) {
  const width = layout.columnWidths[column.id];
  return (
    <Table.Th
      className={`${sticky ? classes.stickyColumnHeader : classes.resizableHeader} ${classes.hasResizeHandle}`}
      // Room for the handle, so it never covers the sort icon.
      style={withColumnWidth({ ...style, paddingRight: 12 }, width)}
    >
      {children}
      <ColumnResizeHandle
        columnId={column.id}
        ariaLabel={`Resize ${column.label} column`}
        width={width}
        onResize={layout.onColumnResize}
        onReset={layout.onColumnResetWidth}
      />
    </Table.Th>
  );
}
